import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { 
  Calendar, Clock, ArrowRight, Search,
  TrendingUp, Zap, Shield, Sparkles,
  ChevronLeft, Share2, Bookmark
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const XIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content?: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  featured?: boolean;
}

const categories = [
  'All',
  'How-To Guides',
  'Product Updates',
  'Automation',
  'Data Security'
];

const blogPosts: BlogPost[] = [
  {
    id: 'how-to-automate-lead-follow-up',
    title: 'How to Automate Lead Follow-Up',
    excerpt: 'Stop manually texting every lead. Learn how to set up automatic follow-ups that handle the heavy lifting for you.',
    content: `
      <h2>The Bottleneck of Manual Outreach</h2>
      <p>Most real estate wholesalers fail not because they can't find leads, but because they can't keep up with them. When you are running marketing across multiple channels—Direct Mail, PPC, and Cold Calling—the volume of inbound responses can quickly become overwhelming. If you are manually typing out every "How much are you offering?" reply, you are already losing. The sheer volume of non-motivated "tire kickers" will drown out the actual deals before you can even identify them.</p>
      
      <h2>The Gold in the First 5 Minutes</h2>
      <p>Data shows that your chances of closing a deal drop by over 400% if you wait more than 5 minutes to respond to an inbound inquiry. Motivated sellers are often in a state of high emotional distress or urgency. They are looking for a solution, and the first person to provide a professional, empathetic response wins the "mental contract" before the physical one is even drafted. Automation isn't about being lazy; it's about being present when the seller is most vulnerable.</p>
      
      <h3>Step 1: The Immediate Multi-Channel Response</h3>
      <p>The moment a lead enters WholeScale OS, your system should trigger an immediate "First Touch." We recommend a combination of a text message and an email. The text should be informal and conversational: <em>"Hi [Name], I just saw your info about the property on [Address]. I'm looking at the area now—do you have a few minutes to chat today?"</em> This creates an immediate "presence" without requiring you to stop what you're doing.</p>
      
      <h3>Step 2: The 7-Day High-Intensity Drip</h3>
      <p>If the lead doesn't reply to the first message, do not give up. Most deals are found between the 5th and 12th touchpoint. Set up an automated sequence that follows this cadence:</p>
      <ul>
        <li><strong>Day 1:</strong> Immediate SMS + Email + Ringless Voicemail (RVM).</li>
        <li><strong>Day 2:</strong> Follow-up SMS in the afternoon asking if they received your previous message.</li>
        <li><strong>Day 4:</strong> "Just checking in" email with a link to your "How it Works" page to build credibility.</li>
        <li><strong>Day 7:</strong> The "Break-up" text: <em>"Hey [Name], I haven't heard back so I'll assume you've moved in a different direction with the property. Best of luck!"</em> (This often triggers a "Wait, I'm still interested!" reply).</li>
      </ul>
      
      <h2>Implementing Conditional Logic</h2>
      <p>The most important part of automation is "Stop on Reply." In WholeScale OS, you must ensure your workflows are set to terminate the moment a lead responds. Nothing kills a deal faster than a seller replying "Yes, call me," and then receiving another automated text 10 minutes later asking if they are still interested. Use the <strong>Automation Hub</strong> to drag and drop these conditions into place to ensure your system behaves with high emotional intelligence.</p>

      <h2>Nurturing the "Not Yet" Lead</h2>
      <p>Not every seller is ready to sign today. In fact, 80% of your deals will come from people who initially say "No" or "Maybe later." Automating a long-term nurture sequence (one touchpoint every 30 days) ensures that when their situation changes—perhaps they finally get tired of managing that vacant rental—your name is the first one they see in their inbox. This "passive pipeline" is the difference between a one-man hustle and a sustainable business.</p>

      <h2>Pro-Tip: Use AI for Triage</h2>
      <p>Instead of sending the same generic follow-up to everyone, use WholeScale's built-in AI to analyze the inbound response. If the AI detects high urgency (e.g., "moving next week"), it can automatically skip the drip and notify your Acquisitions Manager to call immediately. This "Intelligent Automation" ensures that your human energy is always spent on the highest-probability outcomes.</p>
      
      <p>By automating this initial "sifting" process, you free up your time to do what actually makes money: negotiating with motivated sellers and signing contracts. Let the OS handle the noise so you can focus on the signal.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Strategy',
    date: '2026-05-15',
    readTime: '8 min read',
    category: 'Automation',
    image: '',
    featured: true
  },
  {
    id: 'why-speed-matters-in-wholesaling',
    title: 'Why Speed Matters in Wholesaling',
    excerpt: 'In wholesaling, speed is the ultimate competitive advantage. Learn why the fastest investor always wins the deal.',
    content: `
      <h2>The Velocity of Motivation</h2>
      <p>In the world of distressed real estate, motivation is a fleeting emotion. A seller might be ready to give their house away at 2 PM because of a looming foreclosure, but by 6 PM, they might have talked themselves into "trying one more thing." Your window of opportunity is measured in minutes, not days. Speed isn't just about being polite; it's about catching the seller at the peak of their decision-making cycle before their logical "fear" brain overrides their emotional "solution" brain.</p>
      
      <h2>The Competitive Landscape</h2>
      <p>If a seller has filled out your lead form, they have almost certainly filled out the forms of 3 or 4 other investors in your market. Wholesaling is a "winner takes all" game. The investor who sets the appointment first is the one who establishes the initial rapport. Once that rapport is built, the seller is far less likely to entertain offers from latecomers, even if those offers are slightly higher, because they have already begun to trust you as their problem-solver.</p>
      
      <h3>The "First to Sign" Rule</h3>
      <p>Speed should extend beyond just the first phone call. It must permeate your entire process to prevent "contract remorse" or interference from other wholesalers:</p>
      <ul>
        <li><strong>Speed to Underwrite:</strong> Use WholeScale's desktop valuation tools to get a rough MAO (Maximum Allowable Offer) in under 60 seconds. Don't wait for a manual deep dive.</li>
        <li><strong>Speed to Appointment:</strong> If they are motivated, get to the house today. Not tomorrow. Today. Physical presence creates a commitment that digital signatures can't match.</li>
        <li><strong>Speed to Contract:</strong> Carry digital contracts on your tablet. Don't "send it over later." Sign it at the kitchen table. Once the ink is dry, the seller stops looking for other options.</li>
      </ul>
      
      <h2>The Psychology of the Seller</h2>
      <p>Motivated sellers are often overwhelmed by their circumstances. They are looking for the path of least resistance. If you respond instantly, you are signaling that you are a high-capacity professional who can handle their problems. A slow response, on the other hand, signals that you are just another "amateur" who might not actually have the funds or the expertise to close. Your speed is your credibility.</p>

      <h2>Automating the Urgency</h2>
      <p>You can't be at your computer 24/7, but your business can be. Use automated "Urgency Loops" in your CRM. If a lead isn't reached on the first try, the system should trigger a "Call Back" task for your team every 2 hours until contact is made. In WholeScale OS, these tasks are pinned to the top of your dashboard, ensuring that no hot lead is ever allowed to cool down.</p>

      <h2>Measuring Your "Speed-to-Lead"</h2>
      <p>If you don't measure it, you can't improve it. Track your team's average response time across all marketing channels. A world-class wholesaling operation has a "Speed-to-Lead" of under 3 minutes. In WholeScale OS, you can view these metrics in real-time. If your average response time is creeping up, it's time to hire more acquisition support or increase your automation intensity.</p>

      <p>Technology is the great equalizer here. By using automated triage and instant mobile notifications, a solo wholesaler can outpace a large firm that relies on manual processing. Don't let your competition outwork you—outspeed them. The fastest whale in the ocean gets the most food.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Operations',
    date: '2026-05-14',
    readTime: '6 min read',
    category: 'How-To Guides',
    image: '',
    featured: true
  },
  {
    id: 'using-ai-to-sort-leads',
    title: 'Using AI to Sort High-Value Leads',
    excerpt: 'Tired of sorting through "tire kickers"? Use AI to automatically find the most motivated sellers in your list.',
    content: `
      <h2>The Signal-to-Noise Challenge</h2>
      <p>Every wholesaler knows the frustration of "junk leads." You spend thousands on marketing, only to spend your entire day talking to people who want retail price for a house that needs a total gut job. This "noise" drains your energy, kills your team's morale, and prevents you from finding the "signal"—the 5% of leads that will actually turn into a $20,000 assignment fee. Manual sorting is a relic of the past; intelligent triage is the future.</p>
      
      <h2>Sentiment Analysis: Reading Between the Lines</h2>
      <p>WholeScale OS uses advanced NLP (Natural Language Processing) to perform real-time sentiment analysis on every inbound text and email. The AI doesn't just look for keywords like "sell" or "help"; it understands the emotional subtext of the conversation. It can distinguish between someone saying "The roof is old" (minor motivation) and "I can't afford to fix the roof and the bank is calling" (high motivation/distress). This allows you to prioritize your calls based on actual urgency, not just the order of arrival.</p>
      
      <h3>Automated Triage Protocols</h3>
      <p>When a lead is processed by our AI, it is automatically assigned a "Distress Score" from 1-10. Here is how to use that data to maximize your ROI:</p>
      <ul>
        <li><strong>Score 8-10:</strong> Immediate notification to your phone. These leads require a phone call within 2 minutes. These are your "Today" deals.</li>
        <li><strong>Score 5-7:</strong> Assigned to an automated follow-up sequence to "warm up" the lead before you call. These are your "Next Week" deals.</li>
        <li><strong>Score 1-4:</strong> Long-term nurture bucket. These are likely "tire kickers" who might be ready in 6 months. These are your "Passive Pipeline" deals.</li>
      </ul>
      
      <h2>The Human-AI Handover</h2>
      <p>AI is a tool, not a replacement for a closer. The magic happens at the "handover." Once the AI identifies a high-distress lead, it provides you with a summary of *why* they are motivated. "Seller is frustrated with inherited property and wants a cash offer by Friday." When you pick up the phone, you aren't guessing. You are leading with the solution they've already hinted at wanting. This drastically reduces the time spent on discovery calls.</p>

      <h2>Predictive Analytics for Exit Strategies</h2>
      <p>Modern AI can even help you predict which exit strategy will work best for a lead. By analyzing the property data and the seller's sentiment, the OS might suggest "Subject To" for a lead with low equity but high motivation, or a "Quick Cash" wholesale flip for a high-equity, distressed-condition property. This high-level strategy guidance helps you maximize the profit on every lead you touch.</p>

      <h2>Focusing on the 20%</h2>
      <p>The Pareto Principle states that 80% of your profits will come from 20% of your leads. AI allows you to identify that 20% instantly. Instead of calling your lead list in the order the leads came in, you call them in the order of their motivation score. This single change can double your closing rate overnight because you are always talking to the hottest prospects while they are most ready to sell. You're no longer hunting; you're harvesting.</p>
      
      <p>Stop being a data entry clerk and start being a closer. Let AI handle the sorting so you can handle the signing. Your time is worth $500/hr—don't spend it on $15/hr lead sorting tasks.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Product',
    date: '2026-05-12',
    readTime: '7 min read',
    category: 'Automation',
    image: '',
    featured: false
  },
  {
    id: 'building-automated-sms-campaigns',
    title: 'Building Automated SMS Campaigns',
    excerpt: 'A quick guide to setting up your first SMS automation. It is easier than you think.',
    content: `
      <h2>Why SMS is King in Wholesaling</h2>
      <p>Email open rates in the real estate industry hover around 20%. SMS open rates are consistently above 98%. If you want to reach a seller, you have to be in their pocket. Automated SMS campaigns allow you to maintain a "personal" touch with hundreds of leads simultaneously without spending your entire day on your phone. It is the most scalable way to generate "hand-raisers" in today's market.</p>
      
      <h2>Compliance and Ethics (TCPA)</h2>
      <p>Before you send a single text, you must understand compliance. The TCPA (Telephone Consumer Protection Act) is no joke. Ensure your leads have opted-in to receive communications and always include a clear way for them to opt-out (e.g., "Reply STOP to unsubscribe"). WholeScale OS handles the technical side of this, including "Do Not Call" (DNC) list scrubbing and 10DLC registration, so you stay on the right side of the law while you scale.</p>
      
      <h3>Creating Conversational Scripts</h3>
      <p>The secret to high-converting SMS isn't a "sales pitch" or a formal company introduction. It's a short, casual question. Your goal is to get a reply—any reply—to start the conversation. Once they reply, the AI can take over. Use short, open-ended questions that feel like they came from a neighbor:</p>
      <ul>
        <li><em>"Hey [Name], I'm the guy who sent the letter about [Address]. Just wanted to see if you got it?"</em></li>
        <li><em>"Quick question—does the house on [Address] need many repairs or is it in good shape?"</em></li>
        <li><em>"I'm going to be in [Neighborhood] tomorrow looking at another house—any chance I could stop by for 5 mins to see yours?"</em></li>
      </ul>
      
      <h2>The Art of the Subtle Follow-Up</h2>
      <p>The "Second Touch" is where most wholesalers fail. If they don't get a reply, they give up. A subtle, automated follow-up 24 hours later can double your response rate. Something as simple as: <em>"Hey [Name], just checking if you saw my text from yesterday? My phone has been acting up, want to make sure I didn't miss you."</em> This provides a "face-saving" excuse for them to reply if they were busy when you first reached out.</p>

      <h2>Handling Common Objections via AI</h2>
      <p>You can train your SMS automation to handle the most common objections. If a seller replies "How much will you give me?", the system can automatically respond: <em>"I'd love to give you a fair number, but it depends on the condition. Roughly, is the roof/HVAC in good shape, or is it a fixer-upper?"</em> This keeps the ball in their court and gathers critical data while you're focused on other tasks.</p>

      <h2>A/B Testing Your Hook</h2>
      <p>Don't assume your first script is the best. In WholeScale OS, you can split-test your automated campaigns. Send Script A to 50% of your list and Script B to the other 50%. You might find that mentioning a "quick closing" works better in lower-income areas, while focusing on "convenience and no repairs" works better in suburban neighborhoods. Data beats "gut feeling" every time.</p>
      
      <p>Automated SMS is the engine of a modern wholesaling business. Set it up once, tweak your scripts based on performance, and watch your inbox fill up with motivated sellers while you're out doing other things. Scale your outreach, not your workload.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Support',
    date: '2026-05-10',
    readTime: '6 min read',
    category: 'How-To Guides',
    image: '',
    featured: false
  },
  {
    id: 'keeping-data-private-local-ai',
    title: 'Keeping Your Data Private with Local AI',
    excerpt: 'Your lead data is your most valuable asset. Learn why using "Local AI" prevents your competitors from seeing your secrets.',
    content: `
      <h2>The Asset is the Data</h2>
      <p>In wholesaling, your lead list is your moat. If a competitor gets access to your list of motivated sellers, your business value drops to zero. Unfortunately, many modern AI tools require you to upload your data to their cloud servers, where it is often used to "train" their models. In effect, you are paying these big tech companies to help them learn from your secrets so they can eventually sell that intelligence back to your competitors.</p>
      
      <h2>The Hidden Risks of the Cloud</h2>
      <p>When your data lives on a public cloud AI server, it is subject to their security protocols, their terms of service, and their outages. More importantly, it creates a "digital trail" that savvy competitors can sometimes exploit via data scraping or API vulnerabilities. Real real estate "sovereignty" means owning the hardware and the software that processes your most sensitive info. If you don't control the processing, you don't truly own the data.</p>
      
      <h3>What is "Local AI"?</h3>
      <p>Local AI means that the "brain" of the AI—the large language model—runs directly on your own secure server infrastructure (or even your local machine) rather than a shared public cloud. When WholeScale OS analyzes a lead's motivation or summarizes a phone call, that analysis happens inside your "private bubble." No data is sent to OpenAI, Google, or Meta. It stays where it belongs: with you, under your encryption keys.</p>
      
      <h3>Benefits of Data Sovereignty:</h3>
      <ul>
        <li><strong>Zero Leakage:</strong> Your motivated seller lists and negotiation strategies never touch a third-party server.</li>
        <li><strong>Regulatory Compliance:</strong> Easily meet strict data privacy laws (CCPA/GDPR) because you have total end-to-end control of the data lifecycle.</li>
        <li><strong>Offline Capability:</strong> Your AI triage tools work even if the big tech companies have a major server outage, ensuring your business stays online.</li>
        <li><strong>Competitive Advantage:</strong> You are building a private knowledge base of *your* market that your competitors simply cannot buy or replicate.</li>
      </ul>
      
      <h2>The Rise of Data Poisoning</h2>
      <p>As more investors use public AI, those AI models become "average." They start giving the same advice and scripts to everyone. By using Local AI, you can "fine-tune" your model on your own closed-loop data. If you know that sellers in Houston respond better to a certain tone, your Local AI learns that and applies it across your business, creating a unique "House Style" that competitors using generic GPT-4 can't copy.</p>

      <h2>Setup Checklist for Privacy</h2>
      <p>Transitioning to a private AI stack isn't as hard as it sounds. Here is a quick checklist:</p>
      <ul>
        <li><strong>Audit your current stack:</strong> Where is your lead data currently being sent?</li>
        <li><strong>Use End-to-End Encryption:</strong> Ensure any data in transit is encrypted with keys only you hold.</li>
        <li><strong>Switch to WholeScale:</strong> Our "Privacy-First" architecture was built specifically to keep your data local and your insights private.</li>
      </ul>
      
      <h2>Long-Term Asset Protection</h2>
      <p>As AI becomes more integrated into real estate, the value of "clean," private data will skyrocket. By starting with a Local AI architecture like WholeScale OS, you are future-proofing your empire. You aren't just building a wholesaling business; you are building a private data fortress that can withstand the shifts in the tech landscape. In the age of AI, the one who keeps their data private is the one who keeps their profit.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Engineering',
    date: '2026-05-08',
    readTime: '9 min read',
    category: 'Data Security',
    image: '',
    featured: false
  },
  {
    id: 'how-to-scale-your-wholesaling-business',
    title: 'How to Scale Your Wholesaling Business',
    excerpt: 'Scaling requires moving from a "hustle" to a "system." Learn how to build an empire that runs without you.',
    content: `
      <h2>The Ceiling of the Hustle</h2>
      <p>Most wholesalers are stuck in the "Hustle Phase." They are the ones pulling the lists, the ones driving for dollars, and the ones answering the phones at 9 PM. While this is necessary at the start to learn the business, it is a recipe for burnout and stagnation. You cannot scale a business if you are the only person who knows how to run it. Scaling requires moving from being the "worker" in the business to being the "architect" of the system.</p>
      
      <h2>Standard Operating Procedures (SOPs)</h2>
      <p>The first step to scaling is documentation. Every time you do something—underwriting a deal, sending a contract, talking to a title company—write down exactly how you did it. These SOPs become the "instruction manual" for your future team. In WholeScale OS, you can embed these protocols directly into your workflows so your team always knows the next step without having to call you. If a task isn't in an SOP, it doesn't exist.</p>
      
      <h3>The "Three-Team" Model</h3>
      <p>To reach a consistent 7-figure wholesaling business, you need to divide your operation into three distinct departments with clear KPIs:</p>
      <ul>
        <li><strong>Acquisitions:</strong> The "hunters." Their only job is to talk to sellers and get contracts signed. They should never be doing data entry or list pulling.</li>
        <li><strong>Dispositions:</strong> The "sellers." They build the buyer's list, manage the "auction" process, and move the contracts for the highest possible assignment fee.</li>
        <li><strong>Operations/TC:</strong> The "engine room." They handle the paperwork, the title companies, and the software (WholeScale OS) to ensure the machine stays greased and running.</li>
      </ul>
      
      <h2>The Exit Strategy: Your Business is the Product</h2>
      <p>When you scale, stop thinking about the deals as your product. Think of your *business* as the product. A business that requires you to be there to function is just a high-paying job. A business that runs on systems and software is an asset that can be sold. By building your operation inside WholeScale OS, you are creating a "turnkey" system that has value independent of your personal effort.</p>

      <h2>Managing Your Team via Data</h2>
      <p>Once you have a team, you stop managing people and start managing metrics. Use the <strong>WholeScale Dashboard</strong> to monitor your team's performance daily. Who has the highest contract-to-close ratio? Who is letting leads sit for 24 hours? By managing via hard data rather than "gut feeling" or personal relationships, you can identify bottlenecks and fix them before they cost you six figures in lost revenue.</p>

      <h2>The Infinite Scale Loop</h2>
      <p>The secret to infinite scale is reinvestment. Once your system is profitable without your manual labor, you take a portion of those profits and feed them back into the top of the funnel (more marketing). Because your "conversion machine" (WholeScale OS) is efficient, every dollar you put in results in a predictable return. This "loop" is how you grow from 1 deal a month to 20 deals a month without working 20x harder.</p>
      
      <p>Scaling is an intentional choice to build something bigger than yourself. It requires trust in your systems, a commitment to data-driven management, and the courage to step away from the day-to-day "hustle." With the right infrastructure, there is no limit to how large your real estate empire can grow.</p>
    `,
    author: 'The WholeScale Team',
    authorRole: 'Strategy',
    date: '2026-05-05',
    readTime: '10 min read',
    category: 'Automation',
    image: '',
    featured: false
  }
];

const categoryIcons: Record<string, any> = {
  'How-To Guides': ArrowRight,
  'Product Updates': Sparkles,
  'Automation': Zap,
  'Data Security': Shield,
};

function FeaturedPost({ post, onRead }: { post: BlogPost; onRead: (post: BlogPost) => void }) {
  return (
    <motion.article 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="group relative"
    >
      <div className="astral-glass rounded-[2.5rem] border border-white/5 overflow-hidden hover:border-indigo-500/30 transition-all duration-200 hover-lift">
        <div className="aspect-[21/9] bg-gradient-to-br from-indigo-600/20 via-purple-600/10 to-transparent relative flex items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
          <div className="relative z-10 text-center p-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
              <Sparkles size={12} className="animate-pulse" /> Featured
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-[1.1] mb-4 group-hover:text-indigo-300 transition-colors">
              {post.title}
            </h2>
            <p className="text-[#a3aac4] text-lg max-w-2xl mx-auto leading-relaxed">
              {post.excerpt}
            </p>
          </div>
        </div>
        <div className="p-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-xs">
              WS
            </div>
            <div>
              <div className="text-sm font-bold text-white">{post.author}</div>
              <div className="text-[10px] text-[#6d758c] uppercase tracking-widest">{post.authorRole}</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-[#6d758c] text-xs">
            <span className="flex items-center gap-2"><Calendar size={14} /> {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-2"><Clock size={14} /> {post.readTime}</span>
          </div>
          <button 
            onClick={() => onRead(post)}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-widest transition-all hover:scale-105 group/btn"
          >
            Read Article <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function BlogCard({ post, onRead }: { post: BlogPost; onRead: (post: BlogPost) => void }) {
  const CatIcon = categoryIcons[post.category] || TrendingUp;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="group"
    >
      <div 
        className="astral-glass rounded-[2rem] border border-white/5 overflow-hidden hover:border-indigo-500/20 transition-all duration-200 hover-lift h-full flex flex-col cursor-pointer"
        onClick={() => onRead(post)}
      >
        <div className="aspect-[16/9] bg-gradient-to-br from-[#1e293b] to-black relative flex items-center justify-center overflow-hidden">
          <CatIcon size={48} className="text-white/5 group-hover:text-indigo-500/20 transition-colors duration-200" />
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[9px] font-black uppercase tracking-[0.2em]">
              {post.category}
            </span>
          </div>
          <h3 className="text-xl font-black tracking-tight mb-3 leading-snug group-hover:text-indigo-300 transition-colors">
            {post.title}
          </h3>
          <p className="text-[#6d758c] text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-[9px]">
                WS
              </div>
              <div>
                <div className="text-xs font-bold text-[#a3aac4]">{post.author}</div>
                <div className="text-[9px] text-[#6d758c] uppercase tracking-widest">{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
            </div>
            <div className="text-[10px] text-[#6d758c] flex items-center gap-1">
              <Clock size={12} /> {post.readTime}
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const heroReveal = useScrollReveal();

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);

  const handleRead = (post: BlogPost) => {
    setSelectedPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      toast.success('Thanks for subscribing!');
      setEmail('');
    }
  };

  return (
    <div className="bg-black text-[#dee5ff] selection:bg-indigo-500/30 min-h-screen">
      <AnimatePresence mode="wait">
        {selectedPost ? (
          <motion.div 
            key="article-detail"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pt-40 pb-32"
          >
            <div className="max-w-4xl mx-auto px-6">
              <button 
                onClick={() => setSelectedPost(null)}
                className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-black uppercase tracking-widest text-xs mb-12 group"
              >
                <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Intelligence Hub
              </button>

              <article className="space-y-12">
                <header className="space-y-8">
                  <div className="flex items-center gap-4">
                    <span className="px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]">
                      {selectedPost.category}
                    </span>
                    <span className="text-[#6d758c] text-[10px] font-black uppercase tracking-widest">
                      {new Date(selectedPost.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] italic uppercase">
                    {selectedPost.title}
                  </h1>
                  <div className="flex items-center justify-between py-8 border-y border-white/5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black">WS</div>
                      <div>
                        <div className="font-black uppercase italic tracking-tight">{selectedPost.author}</div>
                        <div className="text-xs text-[#6d758c] font-bold uppercase tracking-widest">{selectedPost.authorRole}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <button className="p-3 rounded-xl bg-white/5 border border-white/10 text-[#6d758c] hover:text-white transition-all"><Share2 size={18} /></button>
                      <button className="p-3 rounded-xl bg-white/5 border border-white/10 text-[#6d758c] hover:text-white transition-all"><Bookmark size={18} /></button>
                    </div>
                  </div>
                </header>

                <div 
                  className="prose prose-invert prose-indigo max-w-none 
                    prose-h2:text-3xl prose-h2:font-black prose-h2:italic prose-h2:uppercase prose-h2:tracking-tight prose-h2:mb-8 prose-h2:mt-16
                    prose-h3:text-xl prose-h3:font-black prose-h3:uppercase prose-h3:tracking-widest prose-h3:mb-6 prose-h3:mt-12
                    prose-p:text-[#a3aac4] prose-p:text-lg prose-p:leading-relaxed prose-p:mb-8 prose-p:mt-8
                    prose-ul:text-[#a3aac4] prose-ul:space-y-6 prose-ul:mb-12 prose-li:text-lg
                    prose-strong:text-white prose-strong:font-black"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content || '<p>Detailed analysis of this protocol is currently restricted to elite partners. Contact support for briefing.</p>' }}
                />

                <footer className="pt-20 border-t border-white/5 text-center space-y-12">
                   <div className="space-y-4">
                     <h3 className="text-2xl font-black italic uppercase">Did this help your empire?</h3>
                     <div className="flex items-center justify-center gap-4">
                       <a 
                         href="https://x.com/wholescaleos" 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="px-8 py-4 rounded-2xl bg-white text-black font-black uppercase tracking-widest text-xs hover:bg-white/90 transition-all flex items-center gap-3 shadow-xl hover-lift"
                       >
                         <XIcon size={18} /> Discuss on X
                       </a>
                     </div>
                   </div>
                </footer>
              </article>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="blog-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Hero */}
            <section className="pt-32 pb-16 text-center px-6 relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" />
              <div className="relative z-10 max-w-4xl mx-auto" ref={heroReveal.elementRef}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em] mb-8">
                    <Sparkles size={12} /> Intelligence Hub
                  </div>
                  <h1 className="text-5xl md:text-8xl font-black mb-6 leading-none tracking-tighter pb-1">
                    The <span className="astral-gradient-text">Blog</span>
                  </h1>
                  <p className="text-xl text-[#a3aac4] max-w-2xl mx-auto leading-relaxed font-medium">
                    Insights, strategies, and product updates from the WholeScale engineering and strategy teams.
                  </p>
                </motion.div>
              </div>
            </section>

            {/* Search & Filter */}
            <section className="max-w-7xl mx-auto px-6 mb-16">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="relative w-full md:w-80">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6d758c]" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/5 text-sm text-white placeholder-[#6d758c] focus:border-indigo-500/50 focus:outline-none transition-all"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        activeCategory === cat
                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                          : 'bg-white/5 text-[#6d758c] hover:text-white hover:bg-white/10 border border-white/5'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Featured Posts */}
            <AnimatePresence mode="popLayout">
              {featuredPosts.length > 0 && searchQuery === '' && activeCategory === 'All' && (
                <section className="max-w-7xl mx-auto px-6 mb-16 space-y-8">
                  {featuredPosts.map((post) => (
                    <FeaturedPost key={post.id} post={post} onRead={handleRead} />
                  ))}
                </section>
              )}
            </AnimatePresence>

            {/* Blog Grid */}
            <section className="max-w-7xl mx-auto px-6 mb-32">
              <motion.div 
                layout
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                <AnimatePresence mode="popLayout">
                  {regularPosts.map((post) => (
                    <BlogCard key={post.id} post={post} onRead={handleRead} />
                  ))}
                </AnimatePresence>
              </motion.div>
            </section>

            {/* Empty State */}
            {filteredPosts.length === 0 && (
              <section className="max-w-7xl mx-auto px-6 mb-32">
                <div className="text-center py-20">
                  <div className="w-20 h-20 rounded-3xl bg-white/5 flex items-center justify-center mx-auto mb-6">
                    <Search size={32} className="text-[#6d758c]" />
                  </div>
                  <h3 className="text-xl font-black mb-2">No articles found</h3>
                  <p className="text-[#6d758c]">Try adjusting your search or filter criteria.</p>
                </div>
              </section>
            )}

            {/* Newsletter CTA */}
            <section className="py-32 relative overflow-hidden">
              <div className="absolute inset-0 bg-indigo-600" />
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 via-purple-800 to-indigo-900 opacity-90" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
                className="max-w-3xl mx-auto px-6 text-center relative z-10"
              >
                <h2 className="text-4xl md:text-6xl font-black mb-6 text-white tracking-tighter italic leading-[0.9]">
                  Stay Ahead of the Curve
                </h2>
                <p className="text-indigo-100 text-lg mb-10 max-w-xl mx-auto">
                  Get weekly insights on automation, growth strategies, and product updates delivered to your inbox.
                </p>
                {subscribed ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 rounded-2xl bg-white/10 border border-white/20 text-white font-black uppercase tracking-widest text-sm"
                  >
                    Thanks for subscribing!
                  </motion.div>
                ) : (
                  <form className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-lg mx-auto" onSubmit={handleSubscribe}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full sm:flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-indigo-200/50 focus:outline-none focus:border-white/40 transition-all text-sm"
                    />
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white text-indigo-600 font-black text-sm uppercase tracking-widest hover:bg-indigo-50 transition-all hover:scale-105 shadow-xl"
                    >
                      Subscribe
                    </button>
                  </form>
                )}
              </motion.div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
